import { NetworkStatus, useQuery } from '@apollo/client';
import {
  bankersRound,
  ErrorRetry,
  getUserUnitLabel,
  MilestoneStatusMessages,
  PersonaMessages,
  useAppUser,
  useName,
} from '@bechtel/client-common';
import { GET_WORKPACKAGE_QUANTITY_DETAILS } from '@bechtel/client-gql';
import {
  Box,
  Icon,
  IconSize,
  SetSpacing,
  SideSheet,
  SideSheetBody,
  SideSheetClose,
  SideSheetContentCol,
  SideSheetFooter,
  SideSheetHeader,
  SideSheetSubHeader,
  Spinner,
  SpinnerSize,
  Stat,
  Text,
} from '@bechtel/gds';
import { MilestoneStatusCode, Owner, OwnerDetails, PersonaType, QuantityDetails, WorkPackage } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { map } from 'lodash';
import { FC, Fragment, memo, useMemo } from 'react';
import {
  AvatarRegularStyled,
  BACKGROUND_COLORS,
  OwnerTypeStyled,
  PatternStyled,
  QuantityStatsValuesStyled,
  ShiftBoxStyled,
  ShiftStatusTypeBoxStyled,
  TotalHoursPerSfhitStyled,
  TotalHoursStyled,
  TWTotalHoursStyled,
  UserItemStyled,
  WorkPackageOwnerComponentStyled,
  WorkPackageQuantityStatsStyled,
  WorkPackageQuantityStatsValueStyled,
} from './styled';
import {
  OwnerSectionComponentProps,
  QuantityStatValuesProps,
  WorkPackageOwnerComponentProps,
  WorkPackageQuantityStatsProps,
  WorkPackageQuantityStatsSideSheetProps,
} from './types';

export const WorkPackageQuantityStats: FC<WorkPackageQuantityStatsProps> = memo(({ label, value, onClick }) => {
  return (
    <WorkPackageQuantityStatsStyled>
      <WorkPackageQuantityStatsValueStyled>
        <Text size="600" weight="600" color="grey.1000">
          {value}
        </Text>
        <Icon fillColor="grey.600" size={IconSize.MEDIUM} icon="circle-info" onClick={onClick} />
      </WorkPackageQuantityStatsValueStyled>
      <Text size="300" color="grey.600">
        {label}
      </Text>
    </WorkPackageQuantityStatsStyled>
  );
});

export const WorkPackageOwnerComponent: FC<WorkPackageOwnerComponentProps> = memo(({ owner, isUnitOfMeasureHour }) => {
  const { getFullName } = useName();
  const { i18n } = useLingui();
  const crewMember = owner?.assignedCrewMembers?.crewMember;

  return (
    <WorkPackageOwnerComponentStyled>
      <UserItemStyled>
        <Box mr={{ sm: '400' }}>
          <AvatarRegularStyled
            src={crewMember?.imageUrl ? crewMember?.imageUrl : ''}
            hasImageUrl={crewMember && crewMember.imageUrl ? true : false}
          />
        </Box>
        <Box flexDirection={{ sm: 'column' }}>
          <Box pt={{ sm: '100' }} pb={{ sm: '200' }} style={{ whiteSpace: 'nowrap' }}>
            <Text size="300" color="grey.1000" weight="600">
              {crewMember ? getFullName(crewMember.firstName, crewMember.lastName) : t`Unassigned`}
            </Text>
          </Box>
          <Box pb={{ sm: '200' }}>
            <Text size="200" color="grey.600">
              {owner?.assignedCrewMembers?.persona
                ? i18n._(PersonaMessages[owner?.assignedCrewMembers.persona].id)
                : ''}
            </Text>
          </Box>
        </Box>
      </UserItemStyled>
      <QuantityStatValuesComponent
        totalQuantity={owner?.totalQuantity}
        isUnitOfMeasureHour={isUnitOfMeasureHour}
        backgroundColor={{
          planned: BACKGROUND_COLORS.planned.light,
          claimed: BACKGROUND_COLORS.claimed.light,
          approved: BACKGROUND_COLORS.approved.light,
        }}
      />
    </WorkPackageOwnerComponentStyled>
  );
});

export const QuantityStatValuesComponent: FC<QuantityStatValuesProps> = memo(
  ({ totalQuantity, isUnitOfMeasureHour, backgroundColor, color }) => {
    const { user } = useAppUser();
    const unitLabel = getUserUnitLabel(user, true);

    return (
      <Fragment>
        <QuantityStatsValuesStyled backgroundColor={backgroundColor?.planned} color={color}>
          <Text>
            {isUnitOfMeasureHour
              ? `${totalQuantity?.plannedHours ?? 0} ${t`hrs`}`
              : `${totalQuantity?.plannedQuantity?.quantity ?? 0} ${unitLabel}`}
          </Text>
        </QuantityStatsValuesStyled>
        <QuantityStatsValuesStyled backgroundColor={backgroundColor?.claimed} color={color}>
          <Text>
            {isUnitOfMeasureHour
              ? `${totalQuantity?.claimedHours ?? 0} ${t`hrs`}`
              : `${totalQuantity?.claimedQuantity?.quantity ?? 0} ${unitLabel}`}
          </Text>
        </QuantityStatsValuesStyled>
        <QuantityStatsValuesStyled backgroundColor={backgroundColor?.approved} color={color}>
          <Text>
            {isUnitOfMeasureHour
              ? `${totalQuantity?.approvedHours ?? 0} ${t`hrs`}`
              : `${totalQuantity?.approvedQuantity?.quantity ?? 0} ${unitLabel}`}
          </Text>
        </QuantityStatsValuesStyled>
      </Fragment>
    );
  }
);

export const OwnerSectionComponent: FC<OwnerSectionComponentProps> = memo(
  ({ title, ownerDetails, isUnitOfMeasureHour }) => {
    return (
      <Box flexDirection={{ sm: 'column' }}>
        <OwnerTypeStyled>{title}</OwnerTypeStyled>
        {map(ownerDetails?.owners, (owner: Owner) => {
          return <WorkPackageOwnerComponent owner={owner} isUnitOfMeasureHour={isUnitOfMeasureHour} />;
        })}
        <TotalHoursPerSfhitStyled>
          <Text style={{ flex: 4 }}>{isUnitOfMeasureHour ? t`Total Hours` : t`Total Quantity`}</Text>
          <QuantityStatValuesComponent
            totalQuantity={ownerDetails?.totalQuantity}
            isUnitOfMeasureHour={isUnitOfMeasureHour}
            backgroundColor={{
              planned: BACKGROUND_COLORS.planned.dark,
              claimed: BACKGROUND_COLORS.claimed.dark,
              approved: BACKGROUND_COLORS.approved.dark,
            }}
          />
        </TotalHoursPerSfhitStyled>
      </Box>
    );
  }
);

export const WorkPackageQuantityStatsSideSheet: FC<WorkPackageQuantityStatsSideSheetProps> = memo(
  ({ workPackageId, isUnitOfMeasureHour, onClose }) => {
    const { i18n } = useLingui();
    const { user } = useAppUser();
    const unitLabel = getUserUnitLabel(user);

    const { loading, data, error, refetch, networkStatus } = useQuery<any>(GET_WORKPACKAGE_QUANTITY_DETAILS, {
      variables: {
        ids: [workPackageId],
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    });

    const workPackage: WorkPackage | null =
      data && data?.workPackagesByIds?.length > 0 ? data?.workPackagesByIds[0] : null;
    const quantityDetails: QuantityDetails | null = workPackage?.quantityDetails ? workPackage.quantityDetails : null;

    // Memoize constants that don't change
    const milestoneStatus = useMemo<readonly MilestoneStatusCode[]>(
      () => [MilestoneStatusCode.PLANNED, MilestoneStatusCode.CLAIMED, MilestoneStatusCode.APPROVED],
      []
    );

    const personSortOrder = useMemo<readonly PersonaType[]>(
      () => [PersonaType.SUPERINTENDENT, PersonaType.GENERAL_FOREMAN, PersonaType.FOREMAN, PersonaType.FIELD_ENGINEER],
      []
    );

    // Memoize derived data to avoid recalculation on every render
    const { primaryOwnersDetail, secondaryDayShiftOwnersDetail, secondaryNightShiftOwnersDetail, totalQuantity } =
      useMemo(() => {
        const sortOwnersDetail = (ownersDetail: OwnerDetails | undefined): OwnerDetails | undefined => {
          if (!ownersDetail) return undefined;

          const sortedOwners = [...ownersDetail.owners].sort(
            (a, b) =>
              personSortOrder.indexOf(a?.assignedCrewMembers?.persona as PersonaType) -
              personSortOrder.indexOf(b?.assignedCrewMembers?.persona as PersonaType)
          );

          return {
            ...ownersDetail,
            owners: sortedOwners,
          };
        };

        return {
          primaryOwnersDetail: sortOwnersDetail(quantityDetails?.primaryOwnersDetail),
          secondaryDayShiftOwnersDetail: sortOwnersDetail(quantityDetails?.secondaryDayShiftOwnersDetail),
          secondaryNightShiftOwnersDetail: sortOwnersDetail(quantityDetails?.secondaryNightShiftOwnersDetail),
          totalQuantity: workPackage?.quantityDetails?.totalQuantity,
        };
      }, [quantityDetails, personSortOrder]);

    // Memoize computed values for the header
    const headerStat = useMemo(() => {
      if (!workPackage) return null;

      return {
        label: isUnitOfMeasureHour ? t`Hours Earned vs Total Hours` : t`${unitLabel} Earned vs Total ${unitLabel}`,
        value: isUnitOfMeasureHour
          ? `${bankersRound(workPackage.earnedHours, 0)}/${bankersRound(workPackage.totalEarnableHours, 0)}`
          : `${bankersRound(workPackage.earnedQuantity, 2)}/${bankersRound(workPackage.totalEarnableQuantity.quantity, 2)}`,
      };
    }, [workPackage, isUnitOfMeasureHour, unitLabel]);

    // Memoize rendered milestone status items
    const renderedMilestoneStatuses = useMemo(
      () =>
        milestoneStatus.map((status) => (
          <ShiftStatusTypeBoxStyled key={status}>
            <PatternStyled status={status} />
            <Text size="300" color="grey.600">
              {i18n._(MilestoneStatusMessages[status].id)}
            </Text>
          </ShiftStatusTypeBoxStyled>
        )),
      [milestoneStatus]
    );

    return (
      <SideSheet style={{ width: '30%' }} onDismiss={onClose} visible>
        <SideSheetHeader title={isUnitOfMeasureHour ? t`Hours Details` : t`Quantity Details`}>
          <SideSheetClose onClick={onClose} />
        </SideSheetHeader>
        {headerStat && (
          <SideSheetSubHeader>
            <Box>
              <Stat label={headerStat.label} value={headerStat.value} spacing={SetSpacing.MEDIUM} />
            </Box>
          </SideSheetSubHeader>
        )}
        <SideSheetBody>
          <SideSheetContentCol style={{ scrollbarWidth: 'none' }}>
            {(loading || networkStatus === NetworkStatus.refetch) && (
              <Box
                data-testid="loading"
                alignItems={{ sm: 'center' }}
                justifyContent={{ sm: 'center' }}
                p={{ sm: '500' }}
                style={{ flexGrow: 1 }}
              >
                <Spinner size={SpinnerSize.LARGE} />
              </Box>
            )}
            {error && (
              <ErrorRetry refetch={refetch}>
                <Trans>There was a problem loading Detail. Please click the button below to try again.</Trans>
              </ErrorRetry>
            )}
            {!loading && !error && !workPackage && (
              <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '500' }}>
                <Trans>No detail found.</Trans>
              </Box>
            )}
            {workPackage && (
              <Box flexDirection={{ sm: 'column' }} data-testid="data-secondary-owner-body-parent">
                <ShiftBoxStyled>
                  <Text style={{ flex: 4 }}>
                    <Trans>Shift</Trans>
                  </Text>
                  {renderedMilestoneStatuses}
                </ShiftBoxStyled>
                <Box flexDirection={{ sm: 'column' }}>
                  <OwnerSectionComponent
                    title={t`Primary`}
                    ownerDetails={primaryOwnersDetail}
                    isUnitOfMeasureHour={isUnitOfMeasureHour}
                  />
                  <OwnerSectionComponent
                    title={t`Secondary(Day Shift)`}
                    ownerDetails={secondaryDayShiftOwnersDetail}
                    isUnitOfMeasureHour={isUnitOfMeasureHour}
                  />
                  <OwnerSectionComponent
                    title={t`Secondary(Night Shift)`}
                    ownerDetails={secondaryNightShiftOwnersDetail}
                    isUnitOfMeasureHour={isUnitOfMeasureHour}
                  />
                </Box>
              </Box>
            )}
          </SideSheetContentCol>
        </SideSheetBody>
        {workPackage && (
          <TWTotalHoursStyled>
            <Text style={{ flex: 4 }}>
              {isUnitOfMeasureHour ? t`TEAMWorks Total Hours` : t`TEAMWorks Total Quantity`}
            </Text>
            <QuantityStatValuesComponent
              totalQuantity={quantityDetails?.twQuantitySummary}
              isUnitOfMeasureHour={isUnitOfMeasureHour}
              backgroundColor={{
                planned: BACKGROUND_COLORS.planned.dark,
                claimed: BACKGROUND_COLORS.claimed.dark,
                approved: BACKGROUND_COLORS.approved.dark,
              }}
            />
          </TWTotalHoursStyled>
        )}
        {workPackage && (
          <SideSheetFooter>
            <Box>
              <TotalHoursStyled>
                <Text>{isUnitOfMeasureHour ? t`Total Hours` : t`Total Quantity`}</Text>
              </TotalHoursStyled>
              <QuantityStatValuesComponent
                totalQuantity={totalQuantity}
                isUnitOfMeasureHour={isUnitOfMeasureHour}
                color="white"
              />
            </Box>
          </SideSheetFooter>
        )}
      </SideSheet>
    );
  }
);
